/**
 * Block 5.1 (2026-05-26): Month-Close Runner pages.
 *
 * ч.1028: приведено до канону (Норма #29): `.card` (#5), фільтри у тулбарі
 * як HrOrders, `PeriodFilter` замість числових полів, `NiceSelect`,
 * `DocStatusBadge` для статусів, лише токени теми (#11), кнопки текстом з
 * малої літери (#7). Раніше тут було 7 жорстких кольорів — у нічній темі
 * панелі pre-check і результату світили білим.
 *
 * ч.1120: закриття — документ, який проводять і розпроводять (сторінка
 * документа). Тут: перше закриття місяця, перехід до наявного документа і
 * блок «Заборона змін» — окрема дата, яку закриття не пересуває.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  monthCloseRuns,
  type MonthCloseRun,
  type MonthCloseRunListItem,
} from '@/api/month_close_runs';
import { orgs } from '@/api/catalogs';
import ClosingSnapshotCard from '@/components/dashboard/ClosingSnapshotCard';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocStatusBadge from '@/shared/components/DocStatusBadge';
import {
  badge, formatOpSummary, OP_LABELS, stepDocumentLink, WARNING_LABELS,
} from './monthCloseLabels';
import PeriodLockPanel from './PeriodLockPanel';
import { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useDefaults } from '@/store/defaults';
import AuditBeforeClose from './AuditBeforeClose';


export function MonthCloseRunsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<MonthCloseRun | null>(null);

  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const list = useQuery({
    queryKey: ['month-close-runs', orgId],
    queryFn: () =>
      monthCloseRuns.list({
        organization_id: orgId || undefined,
        limit: 200,
      }),
  });

  const preCheckMut = useMutation({
    mutationFn: () =>
      monthCloseRuns.preCheck({
        organization_id: orgId,
        period_year: year,
        period_month: month,
      }),
  });

  const runMut = useMutation({
    mutationFn: () =>
      monthCloseRuns.run({
        organization_id: orgId,
        period_year: year,
        period_month: month,
      }),
    onMutate: () => setRunning(true),
    onSuccess: (data) => {
      setLastResult(data);
      setRunning(false);
      qc.invalidateQueries({ queryKey: ['month-close-runs'] });
      // Після закриття з'являється підказка «захистити закритий місяць».
      qc.invalidateQueries({ queryKey: ['period-lock'] });
    },
    onError: () => setRunning(false),
  });

  const cols = useMemo<ColumnDef<MonthCloseRunListItem>[]>(
    () => [
      {
        key: 'document_number', header: '№', width: 130, importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'period_label', header: 'Період', width: 140,
        sortValue: (r) => r.period_year * 100 + r.period_month,
        cell: (r) => `${MONTHS_UK[r.period_month] ?? r.period_month} ${r.period_year}`,
      },
      {
        key: 'status', header: 'Статус', width: 130,
        sortValue: (r) => badge(r.status).label,
        cell: (r) => <DocStatusBadge {...badge(r.status)} />,
      },
      {
        key: 'pre_check_passed', header: 'Перевірка', width: 120,
        sortValue: (r) => (r.pre_check_passed ? 1 : 0),
        cell: (r) => (r.pre_check_passed
          ? <DocStatusBadge label="пройдено" tone="green" />
          : <DocStatusBadge label="є зауваження" tone="amber" />),
      },
      {
        key: 'started_at', header: 'Початок', width: 160,
        sortValue: (r) => r.started_at ?? '',
        cell: (r) =>
          r.started_at ? new Date(r.started_at).toLocaleString('uk-UA') : '',
      },
      {
        key: 'completed_at', header: 'Завершено', width: 160,
        sortValue: (r) => r.completed_at ?? '',
        cell: (r) =>
          r.completed_at ? new Date(r.completed_at).toLocaleString('uk-UA') : '',
      },
    ],
    [],
  );

  const canRun = Boolean(orgId && year && month) && !running;
  const pc = preCheckMut.data;
  // ч.1120: за місяць один документ закриття. Якщо він уже є — відкриваємо
  // його (там «розпровести» / «провести» / «повторити невдалі кроки»), а не
  // пропонуємо створити другий.
  const existing = orgId
    ? list.data?.items.find((r) => r.organization_id === orgId
        && r.period_year === year && r.period_month === month)
    : undefined;

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          Закриття місяців<TitleTotal total={list.data?.total ?? null} />
        </h2>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center',
                      flexWrap: 'wrap', marginLeft: 'auto' }}>
          {/* Звичайний <select>: NiceSelect підхоплює його сам (ч.965). */}
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— оберіть організацію —</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <PeriodFilter month={month} year={year}
            onMonth={(v) => setMonth(Number(v) || month)}
            onYear={(v) => setYear(Number(v) || year)} />
          <button type="button" disabled={!canRun}
            title={canRun ? undefined : 'Оберіть організацію і період'}
            onClick={() => preCheckMut.mutate()}>
            перевірити
          </button>
          {existing ? (
            <button type="button" className="primary" data-testid="open-existing-run"
              title="Закриття за цей місяць уже є: у документі його можна розпровести і провести знову"
              onClick={() => navigate(`/period-close/runs/${existing.id}`)}>
              відкрити закриття {existing.document_number}
            </button>
          ) : (
            <button type="button" className="primary" disabled={!canRun}
              title={canRun ? undefined : 'Оберіть організацію і період'}
              onClick={() => runMut.mutate()}>
              {running ? 'виконується…' : 'закрити місяць'}
            </button>
          )}
        </div>
      </div>

      {/* ч.1120: заборона змін — окрема дата організації (не закриття місяця). */}
      <PeriodLockPanel orgId={orgId} />

      {/* ч.1101: аудит проводок перед закриттям — видно стан і можна запустити. */}
      <AuditBeforeClose orgId={orgId} year={year} month={month} />

      {pc && (
        /* ч.1029: два рівні. BLOCKING — закриття дасть невірну ЗП, не пускаємо.
           INFO — закриття коректне, але неповне: показуємо КОГО і ЩО зробити,
           а рішення лишаємо бухгалтеру. Раніше будь-яке зауваження показувалось
           як «помилка» без пояснень і валило запуск. */
        <div className={`mc__panel mc__panel--${pc.passed
          ? (pc.warnings.length ? 'info' : 'ok') : 'warn'}`}>
          <div className="mc__panel-head">
            {pc.passed
              ? (pc.warnings.length
                ? 'Перевірку пройдено — є зауваження, але закривати можна'
                : 'Перевірку пройдено — можна закривати місяць')
              : 'Закриття заблоковано — спершу усуньте перелічене нижче'}
          </div>
          {pc.warnings.map((w, i) => (
            <div key={i} className="mc__warn">
              <div className="mc__warn-head">
                <DocStatusBadge
                  label={w.severity === 'INFO' ? 'до відома' : 'блокує'}
                  tone={w.severity === 'INFO' ? 'amber' : 'red'} />
                <span className="mc__warn-title">
                  {w.title || WARNING_LABELS[w.warning_type] || w.warning_type}: {w.count}
                </span>
              </div>
              {w.hint && <div className="mc__warn-hint">{w.hint}</div>}
              <ul className="mc__warn-list">
                {w.items.slice(0, 10).map((it: Record<string, unknown>, k) => (
                  <li key={k}>
                    {String(it.employee_name || it.external_id || it.employee_id || '')}
                    {it.document_number ? ` — ${String(it.document_number)}` : ''}
                    {it.vacation_start_date ? ` з ${String(it.vacation_start_date)}` : ''}
                    {it.start_date ? ` з ${String(it.start_date)}` : ''}
                  </li>
                ))}
                {w.items.length > 10 && <li>… і ще {w.items.length - 10}</li>}
              </ul>
            </div>
          ))}
        </div>
      )}

      {lastResult && (
        <>
          <div className="mc__panel">
            <div className="mc__panel-head">
              Результат: {lastResult.document_number}
              <DocStatusBadge {...badge(lastResult.status)} />
              <button type="button" className="an__link"
                onClick={() => navigate(`/period-close/runs/${lastResult.id}`)}>
                відкрити документ
              </button>
            </div>
            {lastResult.operations_results.map((op, i) => (
              <div key={i} className="mc__op">
                <DocStatusBadge
                  label={op.status === 'SUCCESS' ? 'виконано'
                    : op.status === 'SKIPPED' ? 'пропущено' : 'помилка'}
                  tone={op.status === 'SUCCESS' ? 'green'
                    : op.status === 'SKIPPED' ? 'gray' : 'red'} />
                <span className="mc__op-name">{OP_LABELS[op.operation_type] ?? op.operation_type}</span>
                {(() => {
                  const link = stepDocumentLink(op.operation_type, op.document_id);
                  return link ? <a href={link.to}>{link.label}</a> : null;
                })()}
                {op.error_message && <span className="mc__op-err">{op.error_message}</span>}
                {op.summary && Object.keys(op.summary).length > 0 && (
                  <span className="mc__op-sum">{formatOpSummary(op.operation_type, op.summary)}</span>
                )}
              </div>
            ))}
          </div>
          <ClosingSnapshotCard snapshot={lastResult.closing_snapshot} />
        </>
      )}

      <DataTable<MonthCloseRunListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Закриттів ще не було. Подвійний клік по рядку відкриває документ: там закриття розпроводять і проводять знову."
        onRowDoubleClick={(r) => navigate(`/period-close/runs/${r.id}`)}
        initialSortKey="document_number"
        initialSortDir="desc"
      />
    </div>
  );
}
